import { useState } from "react";
import type { ReactElement } from "react";
import type { IMatchCommentary, InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { fullName, inningOrdinal, isLegalBall, isLiveInning, isWicketBall, overHeading, overIndexMode, scoreText, sortBalls, teamByName } from "./helpers";
import type { MatchInfo } from "./helpers";
import { BallRow, Crest, Empty, InningSeg } from "./parts";
import { useInningCommentary } from "./useLiveMatch";

type Filter = "all" | "w" | "b";
type Props = { matchId: string; match?: MatchInfo; innings: InningScorecard[]; liveIndex: number; matchLive: boolean; sel: number | null; setSel: (i: number) => void };

const OverEnd = ({ over, heading, team, inProgress }: { over: IMatchCommentary; heading: number; team: string; inProgress: boolean }) => {
  const bats = [over.firstBatsman, over.secondBatsman].filter((b) => b && (b.firstName || b.lastName));
  const bw = over.bowler;
  return (
    <li className="oend">
      <div className="h">
        {inProgress ? <span className="ldot" /> : null}
        {inProgress ? `Over ${heading} in progress` : `End of over ${heading}`}
        <span style={{ fontWeight: 600, color: "var(--muted)" }}>
          {over.overRuns ?? 0} run{over.overRuns === 1 ? "" : "s"}
          {over.overWickets ? ` · ${over.overWickets} wicket${over.overWickets > 1 ? "s" : ""}` : ""}
        </span>
        <span className="sc">
          {team} {over.totalRuns}/{over.totalWickets}
        </span>
      </div>
      <div className="d">
        {bats.map((b, i) => (
          <span key={i}>
            <b>{fullName(b.firstName, b.lastName)}</b> {b.runs}* ({b.balls})
          </span>
        ))}
        {bw && (bw.firstName || bw.lastName) ? (
          <span>
            {fullName(bw.firstName, bw.lastName)}{" "}
            <b>
              {bw.overs}-{bw.maidens}-{bw.runs}-{bw.wickets}
            </b>
          </span>
        ) : null}
      </div>
    </li>
  );
};

const CommentaryTab = ({ matchId, match, innings, liveIndex, matchLive, sel, setSel }: Props) => {
    const [filter, setFilter] = useState<Filter>("all");
  const idx = Math.min(sel ?? liveIndex, Math.max(0, innings.length - 1));
  const inn = innings[idx];
  const live = matchLive && isLiveInning(inn);
  const c = useInningCommentary(matchId, inn?.inningId, live, !!inn);
  if (!innings.length) return <Empty>Commentary will appear once the first ball is bowled.</Empty>;
  const mode = overIndexMode(c.overs);
  const team = teamByName(match, inn.battingTeamName);
  const newest = c.overs[0]?.overNumber;
  const target = idx > 0 && innings[0] ? (innings[0].runs ?? 0) + 1 : 0;

  const rows: ReactElement[] = [];
  let total = 0;
  c.overs.forEach((o) => {
    const balls = sortBalls(o.ballsResponseList ?? []).reverse();
    total += balls.length;
    const inProg = live && o.overNumber === newest && balls.filter(isLegalBall).length < 6;
    const heading = overHeading(o.overNumber, mode);
    if (filter === "all") rows.push(<OverEnd key={`o${o.overNumber}`} over={o} heading={heading} team={team.name || inn.battingTeamName} inProgress={inProg} />);
    balls.forEach((b, i) => {
      if (filter === "w" && !isWicketBall(b)) return;
      if (filter === "b" && !(b.runs_scored === 4 || b.runs_scored === 6 || b.boundary)) return;
      rows.push(<BallRow key={`b${o.overNumber}-${b.ball_id ?? i}`} ball={b} over={o.overNumber} />);
    });
  });

  return (
    <>
      <div className="toolbar">
        <InningSeg innings={innings} match={match} cur={idx} onChange={setSel} />
        <div className="r">
          <div className="iseg">
            {(
              [
                ["all", "All balls"],
                ["w", "Wickets"],
                ["b", "Fours & sixes"],
              ] as [Filter, string][]
            ).map(([k, l]) => (
              <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="box">
        <ul className="cm">
          <li className="ibr">
            <Crest team={team} size={40} />
            {inningOrdinal(idx)} · {team.name || inn.battingTeamName}
            {target ? ` · chasing ${target}` : ""}
            <span className="n">
              {scoreText(inn)} ({inn.over ?? 0}.{inn.ball ?? 0} ov)
            </span>
          </li>
          {c.isLoading ? (
            <li className="empty" style={{ display: "block" }}>
              Loading commentary…
            </li>
          ) : c.isError ? (
            <li className="empty" style={{ display: "block" }}>
              Could not load commentary. Try again later.
            </li>
          ) : rows.length ? (
            rows
          ) : (
            <li className="empty" style={{ display: "block" }}>
              {c.overs.length ? "No balls match this filter in the loaded overs." : "No balls have been bowled in this innings yet."}
            </li>
          )}
        </ul>
        <div className="box-f">
          <span>
            {total} deliveries loaded · newest first{live ? " · updates automatically" : ""}
          </span>
          {c.hasNextPage ? (
            <button type="button" className="link" style={{ marginLeft: "auto" }} disabled={c.isFetchingNextPage} onClick={c.fetchNextPage}>
              {c.isFetchingNextPage ? "Loading earlier overs…" : "Load earlier overs"}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default CommentaryTab;
